import { useState } from "react";
import { Link } from "@inertiajs/react";
import { Settings2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { suggestSchoolRewards, suggestStudentPrizes } from "@/lib/campaignPlanning";
import { formatMoney } from "@/lib/format";
import RewardList from "@/components/schools/rewards/RewardList";
import SuggestedRewards from "@/components/schools/rewards/SuggestedRewards";
import OtherWaysToReward from "@/components/schools/rewards/OtherWaysToReward";
import PrizeFeatureRequest from "@/components/schools/rewards/PrizeFeatureRequest";
import { DoneSummary } from "@/components/schools/setup/StepPanel";
import { GuideTip, StepNotice } from "@/components/schools/setup/SetupPrimitives";
import { helpHref } from "@/components/schools/setup/steps";

// ── 7. School rewards ─────────────────────────────────────────

export function SchoolRewardsStep({ step, setup, onSelect }) {
  const { school_rewards: rewards, campaign_details: campaign, paths } = setup;
  const [showIdeas, setShowIdeas] = useState(rewards.length === 0);
  if (step.status === "blocked") return null;

  const suggestions = suggestSchoolRewards({ moneyGoalCents: campaign.money_goal_cents, minuteGoal: campaign.minute_goal });
  const goalText = [
    campaign.money_goal_cents && formatMoney(campaign.money_goal_cents),
    campaign.minute_goal && `${campaign.minute_goal.toLocaleString("en-CA")} minutes`,
  ].filter(Boolean).join(" and ");

  return (
    <div className="space-y-6">
      {rewards.length > 0 && (
        <DoneSummary
          title={`${rewards.length} school reward${rewards.length === 1 ? "" : "s"}`}
          actions={<RewardActions managePath={paths.milestones} showIdeas={showIdeas} onToggleIdeas={() => setShowIdeas(!showIdeas)} />}
        >
          <RewardList rewards={rewards} />
        </DoneSummary>
      )}

      {showIdeas && (suggestions.length > 0 ? (
        <SuggestedRewards
          title={`Rewards sized to your ${goalText} goal`}
          description="A quick first win, a halfway treat, and a big finish when you hit the goal."
          suggestions={suggestions}
          appliesTo="campaign"
          path={paths.create_rewards}
          onAdded={() => setShowIdeas(false)}
          noun="reward"
        />
      ) : (
        <StepNotice action={<Button size="sm" variant="outline" onClick={() => onSelect("campaign")}>Set a goal</Button>}>
          Set a fundraising or reading goal and we'll suggest rewards sized to it. Or{" "}
          <Link href={paths.milestones} className="font-medium text-primary hover:underline">add your own</Link>.
        </StepNotice>
      ))}

      <PrizeFeatureRequest />

      <div className="grid gap-4 md:grid-cols-2">
        <GuideTip title="Name them the way a student would" href={helpHref("setting-school-milestones")} linkLabel="More reward ideas">
          “Extra recess” beats “30 extra minutes outside.” Keep them things you can actually deliver, and check the budget and school approval before announcing any prize.
        </GuideTip>
        <GuideTip title="Low-cost ideas that work" href="/readathon-fundraising-ideas" linkLabel="18 readathon ideas">
          The class votes on the next read-aloud. The principal performs a story in character. A read-in afternoon with cushions and comfy clothes.
        </GuideTip>
      </div>
    </div>
  );
}

// ── 8. Student prizes ─────────────────────────────────────────

export function StudentPrizesStep({ step, setup }) {
  const { student_prizes: prizes, campaign_details: campaign, paths } = setup;
  const [showIdeas, setShowIdeas] = useState(prizes.length === 0);
  if (step.status === "blocked") return null;

  const suggestions = suggestStudentPrizes({ start: campaign.start_on, end: campaign.end_on });

  return (
    <div className="space-y-6">
      {prizes.length > 0 && (
        <DoneSummary
          title={`${prizes.length} student prize${prizes.length === 1 ? "" : "s"}`}
          actions={<RewardActions managePath={paths.student_milestones} showIdeas={showIdeas} onToggleIdeas={() => setShowIdeas(!showIdeas)} />}
        >
          <RewardList rewards={prizes} />
        </DoneSummary>
      )}

      {showIdeas && (
        <SuggestedRewards
          title="Three tiers for your campaign's length"
          description="One almost everyone can reach, one that's a stretch, and one that's a real achievement. Tiers stack: a student who reaches the top one earns all three."
          suggestions={suggestions}
          appliesTo="student"
          path={paths.create_rewards}
          onAdded={() => setShowIdeas(false)}
          noun="prize"
        />
      )}

      <OtherWaysToReward studentPrizesPath={paths.student_milestones} />

      <PrizeFeatureRequest />

      <div className="grid gap-4 md:grid-cols-2">
        <GuideTip title="Two or three prizes is plenty" href={helpHref("setting-student-prizes")} linkLabel="How student prizes work">
          Name them specifically: “fidget spinner” gets a student more excited than “small toy.” Teachers mark each prize handed out, so pick things you'll have on hand.
        </GuideTip>
        <GuideTip title="Think about prizes for money raised" href="/readathon-fundraising-ideas" linkLabel="From our ideas guide">
          It's your call. Consider how it will feel to children whose families have different resources. The suggestions here reward reading, so every child can earn them.
        </GuideTip>
      </div>
    </div>
  );
}

// ── Shared ────────────────────────────────────────────────────

function RewardActions({ managePath, showIdeas, onToggleIdeas }) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" asChild>
        <Link href={managePath}>
          <Settings2 className="h-4 w-4" />
          Manage
        </Link>
      </Button>
      <Button size="sm" variant="ghost" onClick={onToggleIdeas}>
        <Wand2 className="h-4 w-4" />
        {showIdeas ? "Hide suggestions" : "More suggestions"}
      </Button>
    </div>
  );
}
